import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';

const categories = [
  {
    label: 'Living Room',
    sub: 'Sofas, Sectionals & Tables',
    path: '/collections/living-room',
    icon: 'ri-sofa-line',
    image: 'https://public.readdy.ai/ai/img_res/8051673853c1041493214565980957b1.jpg',
    accent: 'from-dark/70',
  },
  {
    label: 'Bedroom',
    sub: 'Beds, Dressers & Nightstands',
    path: '/collections/bedroom',
    icon: 'ri-hotel-bed-line',
    image: 'https://readdy.ai/api/search-image?query=serene%20luxury%20master%20bedroom%20with%20upholstered%20platform%20bed%20linen%20bedding%20warm%20neutral%20tones%20natural%20light%20minimalist%20elegant%20bedside%20tables%20high-end%20residential%20photography&width=600&height=400&seq=cat-bed-2026&orientation=landscape',
    accent: 'from-dark/65',
  },
  {
    label: 'Dining Room',
    sub: 'Tables, Chairs & Sideboards',
    path: '/collections/dining-room',
    icon: 'ri-restaurant-line',
    image: 'https://readdy.ai/api/search-image?query=sophisticated%20open%20dining%20room%20with%20long%20oval%20marble%20dining%20table%20sculptural%20dining%20chairs%20warm%20pendant%20lighting%20wood%20and%20stone%20textures%20modern%20luxury%20interior&width=600&height=400&seq=cat-din-2026&orientation=landscape',
    accent: 'from-dark/70',
  },
  {
    label: 'Accent Pieces',
    sub: 'Decor, Lighting & Rugs',
    path: '/collections',
    icon: 'ri-price-tag-3-line',
    image: 'https://readdy.ai/api/search-image?query=elegant%20home%20accessories%20accent%20table%20lamp%20artisan%20vase%20textured%20cushions%20woven%20throws%20warm%20interior%20styling%20beautiful%20curated%20home%20decor%20lifestyle%20product%20photography&width=600&height=400&seq=cat-acc-2026&orientation=landscape',
    accent: 'from-dark/60',
  },
];

export default function ShopByCategory() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const o = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) ref.current?.classList.add('visible');
    }, { threshold: 0.1 });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);

  return (
    <section className="py-28 bg-white">
      <div ref={ref} className="fade-up max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-gold" />
              <span className="text-gold text-xs font-bold uppercase tracking-[0.25em]">Explore</span>
            </div>
            <h2 className="font-serif text-5xl md:text-6xl font-bold text-dark leading-tight">
              Shop by
              <br />
              <em className="text-gold not-italic">Category</em>
            </h2>
          </div>
          <div className="max-w-xs">
            <p className="text-dark/50 text-sm leading-relaxed">
              Furniture for every room in your home — curated by our expert design team for quality and style.
            </p>
            <Link
              to="/collections"
              className="inline-flex items-center gap-2 mt-4 text-dark text-sm font-semibold hover:text-gold transition-colors cursor-pointer"
            >
              Browse All Collections
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </Link>
          </div>
        </div>

        {/* Asymmetric Grid */}
        <div className="grid grid-cols-12 gap-5">
          {/* Living Room — tall left card */}
          <div className="col-span-12 md:col-span-5 group relative overflow-hidden rounded-2xl h-[540px] cursor-pointer">
            <img
              src={categories[0].image}
              alt="Living room furniture store near me Las Vegas"
              title="Living Room Furniture - Eclectic Home"
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
            />
            <div className={`absolute inset-0 bg-gradient-to-t ${categories[0].accent} via-dark/15 to-transparent`} />
            <div className="absolute inset-0 flex flex-col justify-between p-8">
              <div className="self-start bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${categories[0].icon} text-gold text-xs`} />
                </div>
                <span className="text-white text-[10px] font-bold uppercase tracking-[0.2em]">{categories[0].label}</span>
              </div>
              <div>
                <h3 className="font-serif text-3xl font-bold text-white mb-2">{categories[0].label}</h3>
                <p className="text-white/60 text-sm mb-5">{categories[0].sub}</p>
                <Link
                  to={categories[0].path}
                  className="inline-flex items-center gap-2 bg-gold text-white px-6 py-3 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-gold-dark transition-colors cursor-pointer whitespace-nowrap"
                >
                  Shop Now
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-line" />
                  </div>
                </Link>
              </div>
            </div>
          </div>

          {/* Right column — stacked 3 */}
          <div className="col-span-12 md:col-span-7 flex flex-col gap-5">
            {categories.slice(1).map((cat) => (
              <div key={cat.label} className="group relative overflow-hidden rounded-2xl h-[160px] cursor-pointer">
                <img
                  src={cat.image}
                  alt={`${cat.label} furniture modern high-quality`}
                  title={`${cat.label} - Eclectic Home`}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className={`absolute inset-0 bg-gradient-to-r ${cat.accent} via-dark/30 to-transparent`} />
                <div className="absolute inset-0 flex items-center justify-between px-8">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-gold text-sm`} />
                      </div>
                      <span className="text-gold text-[10px] font-bold uppercase tracking-[0.2em]">{cat.sub}</span>
                    </div>
                    <h3 className="font-serif text-2xl font-bold text-white">{cat.label}</h3>
                  </div>
                  <Link
                    to={cat.path}
                    className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-white/15 hover:bg-gold text-white border border-white/20 hover:border-gold transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            ))}

            {/* Bottom CTA card */}
            <div className="bg-cream-mid rounded-2xl p-8 flex items-center justify-between border border-cream-dark">
              <div>
                <div className="text-gold text-xs font-bold uppercase tracking-[0.2em] mb-1">Free Service</div>
                <h4 className="font-serif text-xl font-bold text-dark">Personal Design Consultation</h4>
                <p className="text-dark/50 text-sm mt-1">In-showroom or virtual — always free</p>
              </div>
              <Link
                to="/showroom"
                className="flex-shrink-0 bg-dark text-white px-6 py-3 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-gold transition-colors cursor-pointer whitespace-nowrap"
              >
                Book Now
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
